<template>
  <div class="col-md-8 col-lg-8 offset-md-2 offset-lg-2 page">
    <div class="form" ref="formContainer">
      <h1 class="profile__header mt-md-0 mt-0">CHANGE PASSWORD</h1>

      <form @submit.prevent="handleSubmit" id="form_change_password">
        <div class="form-group">
          <label for="currentPassword" class="profile__label">CURRENT PASSWORD</label>
          <input
            id="currentPassword"
            v-model="form.currentPassword"
            type="password"
            class="form-control profile_input"
            autocomplete="current-password"
            required
          />
          <form-error :errors="formErrors.currentPassword" />
        </div>

        <div class="form-group">
          <label for="password" class="profile__label">NEW PASSWORD</label>
          <input
            id="password"
            v-model="form.password"
            type="password"
            class="form-control profile_input"
            autocomplete="new-password"
            required
            @change="validatePassword"
          />
          <form-error :errors="formErrors.password" />
        </div>

        <div class="form-group">
          <label for="password2" class="profile__label">CONFIRM NEW PASSWORD</label>
          <input
            id="password2"
            v-model="form.password2"
            type="password"
            class="form-control profile_input"
            autocomplete="new-password"
            required
            @change="confirmPassword"
          />
          <form-error :errors="formErrors.password2" />
        </div>

        <div class="password-requirements">
          <p class="password-requirements__title">Password requirements</p>
          <ul>
            <li v-for="(req, index) in passwordRequirements" :key="index">{{ req }}</li>
          </ul>
        </div>

        <div v-if="statusMessage" class="alert alert-warning" role="alert">
          {{ statusMessage }}
        </div>

        <div class="btn-container">
          <router-link to="/user/profile" class="btn btn-secondary profile cancel-link">
            Cancel
          </router-link>
          <button class="btn btn-primary float-end profile submit" type="submit" :disabled="isSubmitting">
            {{ isSubmitting ? "Saving..." : "Save Password" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { changePassword } from "@/api/userApi";
import FormError from "@/components/includes/form/FormError";
import VALIDATION_ERRORS from "@/data/validationErrors";
import { getPasswordPolicyErrors } from "@/utils/passwordPolicy";
import { isViewingPatientChart as sessionIsViewingPatientChart } from "@/data/getters";
import { mapGetters } from "vuex";

export default {
  name: "change-password-page",
  components: {
    "form-error": FormError,
  },
  data() {
    return {
      form: {
        currentPassword: "",
        password: "",
        password2: "",
      },
      formErrors: {
        currentPassword: [],
        password: [],
        password2: [],
      },
      statusMessage: "",
      isSubmitting: false,
      passwordRequirements: [
        VALIDATION_ERRORS.PASSWORD_LENGTH_MIN,
        VALIDATION_ERRORS.PASSWORD_REQUIRE_UPPER,
        VALIDATION_ERRORS.PASSWORD_REQUIRE_LOWER,
        VALIDATION_ERRORS.PASSWORD_REQUIRE_DIGIT,
        VALIDATION_ERRORS.PASSWORD_REQUIRE_SYMBOL,
        VALIDATION_ERRORS.PASSWORD_INVALID_CHAR,
        VALIDATION_ERRORS.PASSWORD_CONTAINS_NAME,
      ],
    };
  },
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    nameParts() {
      const patient = this.allData?.patient || {};
      return [patient.firstName, patient.middleName, patient.lastName].filter(Boolean);
    },
    isViewingPatientChart() {
      return !!sessionIsViewingPatientChart(this.allData);
    },
  },
  mounted() {
    if (this.isViewingPatientChart) {
      this.$router.replace("/welcome");
    }
  },
  methods: {
    validatePassword() {
      this.formErrors.password = getPasswordPolicyErrors(this.form.password, this.nameParts);
      if (this.form.password2) {
        this.confirmPassword();
      }
    },
    confirmPassword() {
      this.formErrors.password2 = [];
      if (this.form.password !== this.form.password2) {
        this.formErrors.password2.push(VALIDATION_ERRORS.PASSWORD_MISMATCH);
      }
    },
    formIsValid() {
      this.formErrors.currentPassword = [];
      if (!this.form.currentPassword) {
        this.formErrors.currentPassword.push("Current password is required.");
      }
      this.validatePassword();
      this.confirmPassword();
      return (
        !this.formErrors.currentPassword.length &&
        !this.formErrors.password.length &&
        !this.formErrors.password2.length
      );
    },
    async handleSubmit() {
      if (this.isSubmitting) return;
      this.statusMessage = "";
      if (!this.formIsValid()) return;

      this.isSubmitting = true;
      try {
        await changePassword({
          currentPassword: this.form.currentPassword,
          password: this.form.password,
          password2: this.form.password2,
        });
        this.$swal({
          type: "success",
          title: "Password updated",
          text: "Your password has been changed successfully.",
        }).then(() => {
          this.$router.push("/user/profile");
        });
      } catch (err) {
        this.statusMessage =
          typeof err === "string" ? err : err?.message || "Unable to change password.";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
$color-base: #0060ff;

.page {
  background-color: #fff;
  margin: 1.5rem auto;
  padding: 2rem;
  margin-bottom: 100px;
}

.profile__header {
  font-size: 1.5rem;
  color: $color-base;
  text-align: center;
  margin-top: 1.5rem;
  margin-bottom: 1em;
}

.profile__label {
  font-size: 0.8rem;
  color: $color-base;
}

input {
  font-size: 0.8rem;
}

.btn-container {
  margin-top: 1.5rem;
  padding-bottom: 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;

  .btn {
    font-size: 0.8rem;
  }
}

.cancel-link {
  text-decoration: none;
}

.password-requirements {
  margin-top: 1.25rem;
  padding: 1rem 1.25rem;
  background: #f7f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;

  &__title {
    margin: 0 0 0.5rem;
    font-size: 0.85rem;
    font-weight: 600;
    color: $color-base;
  }

  ul {
    margin: 0;
    padding-left: 1.25rem;
    font-size: 0.8rem;
    color: #334155;
  }

  li + li {
    margin-top: 0.25rem;
  }
}
</style>
